import {
  AppCard,
  Button,
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiContentSaveOutline } from "@mdi/js";
import { Box, CircularProgress } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  getShowIssuesOfRfqRequestQueryKey,
  useShowIssuesOfRfqRequest,
  useUpdateIssuesOfRfqRequest,
} from "../../../../../__generated__/orval/endpoints/issues-of-rfq-requests/issues-of-rfq-requests";
import { ConfirmationModal } from "../../../../../components/molecules/ConfirmationModal";
import { IssuesOfRfqForm } from "../-internal-components/IssuesOfRfqForm";
import {
  type IssuesOfRfqSchema,
  issuesOfRfqSchema,
} from "../-schemas/issuesOfRfqSchema";

const toIsoOrUndefined = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const toDateTimeLocal = (value?: string): string => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const pad = (part: number): string => String(part).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const toPreviousTicketId = (value?: string): number | undefined => {
  if (!value) {
    return undefined;
  }

  const id = Number(value);

  return Number.isFinite(id) ? id : undefined;
};

const toMatnumbers = (
  values?: Array<string | { matnumber: string }>,
): string[] | undefined => {
  if (!values?.length) {
    return undefined;
  }

  const matnumbers = values
    .map((value) => (typeof value === "string" ? value : value.matnumber))
    .map((value) => value.trim())
    .filter((value) => value.length > 0);

  return matnumbers.length ? matnumbers : undefined;
};

const EditIssuesOfRfqRequest: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { issuesOfRfqRequestId } = Route.useParams();
  const id = Number(issuesOfRfqRequestId);
  const queryClient = useQueryClient();

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [formData, setFormData] = useState<IssuesOfRfqSchema | null>(null);

  const { control, handleSubmit, reset, setValue } = useForm({
    defaultValues: {
      backup_requester: null,
      closing_date: "",
      description: "",
      discipline: null,
      file_attachments: [],
      identified_problem_type: null,
      m5: "",
      matnumber: [],
      opening_date: "",
      previous_ticket: "",
      purchase_order: "",
      purchase_requisition: "",
      requester_field: null,
      ritm: "",
      service_now_reason: null,
      step_purchase: null,
      subject: "",
      technical_contact: "",
      value_current: "",
      value_new: "",
      vendor: null,
      work_order_number: "",
      work_order_responsible_person: null,
    },
    resolver: zodResolver(issuesOfRfqSchema),
  });

  const { data, isLoading, isError } = useShowIssuesOfRfqRequest(id);
  const mmRequestId = data?.mm_request?.id;
  const title = mmRequestId
    ? `${t("common.issuesOfRfq")} #${mmRequestId}`
    : t("common.issuesOfRfq");

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(t("mmRequests.fetchError"), { variant: "error" });
    }
  }, [isError, t]);

  useEffect(() => {
    if (!data) return;

    const mmRequest = data.mm_request as typeof data.mm_request & {
      previous_ticket_id?: number | null;
    };

    const matnumber = Array.isArray(mmRequest.matnumber)
      ? mmRequest.matnumber.filter((value) => value.length > 0)
      : [];

    reset({
      backup_requester: mmRequest.backup_requester
        ? {
            full_name_with_login:
              mmRequest.backup_requester.full_name_with_login,
            id: mmRequest.backup_requester.id,
          }
        : null,
      closing_date: toDateTimeLocal(data.closing_date),
      description: mmRequest.description ?? "",
      discipline: (data.discipline || null) as IssuesOfRfqSchema["discipline"],
      file_attachments: [],
      identified_problem_type: data.identified_problem_type ?? null,
      m5: data.m5 !== undefined && data.m5 !== null ? String(data.m5) : "",
      matnumber,
      opening_date: toDateTimeLocal(data.opening_date),
      previous_ticket: mmRequest.previous_ticket_id
        ? String(mmRequest.previous_ticket_id)
        : "",
      purchase_order: data.purchase_order ?? "",
      purchase_requisition: data.purchase_requisition ?? "",
      requester_field: mmRequest.requester_field
        ? { id: mmRequest.requester_field, name: mmRequest.requester_field }
        : null,
      ritm: data.ritm ?? "",
      service_now_reason: (data.service_now_reason ||
        null) as IssuesOfRfqSchema["service_now_reason"],
      step_purchase: (data.step_purchase ||
        null) as IssuesOfRfqSchema["step_purchase"],
      subject: mmRequest.subject ?? "",
      technical_contact: data.technical_contact ?? "",
      value_current: data.value_current ?? "",
      value_new: data.value_new ?? "",
      vendor: data.vendor_id
        ? { fullvendorname: data.vendor_name ?? "", id: data.vendor_id }
        : null,
      work_order_number: data.work_order_number ?? "",
      work_order_responsible_person: data.work_order_responsible_person
        ? {
            full_name_with_login:
              data.work_order_responsible_person.full_name_with_login,
            id: data.work_order_responsible_person.id,
          }
        : null,
    });
  }, [data, reset]);

  const { mutate, isPending } = useUpdateIssuesOfRfqRequest({
    mutation: {
      onError: () => {
        enqueueSnackbar(t("mmRequestChild.updateErrorMessage"), {
          variant: "error",
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getShowIssuesOfRfqRequestQueryKey(id),
        });
        enqueueSnackbar(t("mmRequestChild.updateSuccessMessage"), {
          variant: "success",
        });
        navigate({
          params: { issuesOfRfqRequestId },
          to: "/vbuyer/issues_of_rfq_requests/$issuesOfRfqRequestId",
        });
      },
    },
  });

  const onSubmit: SubmitHandler<IssuesOfRfqSchema> = useCallback((values) => {
    setFormData(values);
    setOpenConfirmModal(true);
  }, []);

  const handleConfirm = useCallback(() => {
    if (!formData) return;

    setOpenConfirmModal(false);

    const requestData = {
      issues_of_rfq_request: {
        closing_date: toIsoOrUndefined(formData.closing_date),
        discipline: formData.discipline ?? undefined,
        identified_problem_type_id: formData.identified_problem_type?.id,
        m5: formData.m5 ? Number(formData.m5) : undefined,
        mm_request_attributes: {
          description: formData.description,
          matnumber: toMatnumbers(formData.matnumber),
          previous_ticket_id: toPreviousTicketId(formData.previous_ticket),
          requester_field: formData.requester_field?.name ?? "",
          subject: formData.subject,
        },
        opening_date: toIsoOrUndefined(formData.opening_date),
        purchase_order: formData.purchase_order || undefined,
        purchase_requisition: formData.purchase_requisition || undefined,
        ritm: formData.ritm || undefined,
        service_now_reason: formData.service_now_reason ?? undefined,
        step_purchase: formData.step_purchase ?? undefined,
        technical_contact: formData.technical_contact || undefined,
        value_current: formData.value_current || undefined,
        value_new: formData.value_new || undefined,
        vendor_id: formData.vendor?.id,
        work_order_number: formData.work_order_number || undefined,
        work_order_responsible_person_id:
          formData.work_order_responsible_person?.id,
      },
    };

    mutate({
      data: requestData as unknown as Parameters<typeof mutate>[0]["data"],
      id,
    });
  }, [formData, id, mutate]);

  const handleFilesChange = useCallback(() => {}, []);

  if (isLoading) {
    return (
      <PageContainer
        backTo={`/vbuyer/issues_of_rfq_requests/${issuesOfRfqRequestId}`}
        maxWidth="md"
        showBackButton
        title={title}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      backTo={`/vbuyer/issues_of_rfq_requests/${issuesOfRfqRequestId}`}
      maxWidth="md"
      showBackButton
      title={title}
    >
      <AppCard>
        <IssuesOfRfqForm
          control={control}
          onFilesChange={handleFilesChange}
          onSubmit={handleSubmit(onSubmit)}
          setValue={setValue}
          showBackupRequester={false}
          showFileUpload={false}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              color="success"
              loading={isPending}
              startIcon={<Icon icon={mdiContentSaveOutline} />}
              type="submit"
            >
              {t("common.save")}
            </Button>
          </Box>
        </IssuesOfRfqForm>
      </AppCard>

      <ConfirmationModal
        handleOk={handleConfirm}
        message={t("mmRequestChild.updateModalMessage")}
        open={openConfirmModal}
        setOpen={setOpenConfirmModal}
        title={t("mmRequestChild.updateModalTitle")}
      />
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/issues_of_rfq_requests/$issuesOfRfqRequestId/edit",
)({
  component: EditIssuesOfRfqRequest,
});
